import { useCallback, useState } from "react";
import * as THREE from "three";
import { ref, update, push, remove } from "firebase/database";
import { database } from "../firebase";
import { useDispatch, useSelector } from "react-redux";
import { updateSurgeryPos } from "../redux/casesRedux";

const useSurgeryPos = (
  cameraRef,
  controlsRef,
  modelRef,
  undoStack,
  undoIndex,
  selectedCase,
  performDrillAuto,
  performScalpelAutoMultiple,
  applyOpacityByIndex,
  applyVisibilityByIndex
) => {
  const [isSurgeryPosMode, setIsSurgeryPosMode] = useState(false);
  const [selectedSurgeryPosId, setSelectedSurgeryPosId] = useState(null);
  const [isAddSurgeryPosModalOpen, setIsAddSurgeryPosModalOpen] =
    useState(false);
  const dispatch = useDispatch();
  const selectedCaseDetail = useSelector(
    (state) => state.cases.selectedCaseDetail
  );

  const snapshotOpacitiesByName = useCallback(() => {
    const map = {};
    if (!modelRef?.current) return map;
    modelRef.current.children.forEach((child) => {
      if (!child?.material) return;
      const m = Array.isArray(child.material)
        ? child.material[0]
        : child.material;
      const op = typeof m?.opacity === "number" ? m.opacity : 1;
      map[child.name] = op;
    });
    return map;
  }, [modelRef]);

  const applyOpacitiesFromMap = useCallback(
    (opacitiesMap) => {
      if (!modelRef?.current || !opacitiesMap) return;
      modelRef.current.children.forEach((child, idx) => {
        const op = opacitiesMap[child.name];
        if (typeof op !== "number") return;
        const makeVisible = op > 0;
        applyVisibilityByIndex(idx, makeVisible);
        applyOpacityByIndex(idx, op);
      });
    },
    [modelRef, applyVisibilityByIndex, applyOpacityByIndex]
  );

  const goToSurgeryPosition = useCallback(
    (posId) => {
      const surgeryPositions = selectedCaseDetail?.surgeryPos;
      if (!surgeryPositions || !posId || !surgeryPositions[posId]) return;

      const surgeryPos = surgeryPositions[posId];
      const { actions, cameraPos } = surgeryPos;

      // reset history BEFORE replay (replays shouldn't be undoable)
      undoStack.current = [];
      undoIndex.current = 0;

      if (actions && Array.isArray(actions)) {
        actions.forEach((action) => {
          if (action.type === "drill") {
            const p = action.pointLocal,
              n = action.normalLocal;
            if (!p || !n) return;
            performDrillAuto(
              action.geometryId,
              new THREE.Vector3(p.x, p.y, p.z),
              action.brushSize,
              action.drillShape || "circle",
              { pushUndo: false, normalLocal: new THREE.Vector3(n.x, n.y, n.z) }
            );
          } else if (action.type === "scalpel") {
            performScalpelAutoMultiple(action);
          }
        });
      }

      if (cameraPos) {
        cameraRef.current.position.set(
          cameraPos.position.x,
          cameraPos.position.y,
          cameraPos.position.z
        );
        controlsRef.current.target.set(
          cameraPos.target.x,
          cameraPos.target.y,
          cameraPos.target.z
        );
        cameraRef.current.quaternion.set(
          cameraPos.quaternion.x,
          cameraPos.quaternion.y,
          cameraPos.quaternion.z,
          cameraPos.quaternion.w
        );
        cameraRef.current.up.set(
          cameraPos.up.x,
          cameraPos.up.y,
          cameraPos.up.z
        );
        cameraRef.current.lookAt(controlsRef.current.target);
        controlsRef.current.update();
      }

      if (surgeryPos?.opacities) applyOpacitiesFromMap(surgeryPos.opacities);
    },
    [
      selectedCaseDetail,
      performDrillAuto,
      performScalpelAutoMultiple,
      undoStack,
      undoIndex,
      cameraRef,
      controlsRef,
      applyOpacitiesFromMap,
    ]
  );

  // helper: build deterministic actions from undo items
  function buildDeterministicActions(actions) {
    return actions
      .filter((a) => a.type === "drill" || a.type === "scalpel")
      .map((action) => {
        if (action.type === "drill") {
          if (!action.intersectPointLocal || !action.normalLocal) return null;
          return {
            type: "drill",
            geometryId: action.geometryId,
            pointLocal: {
              x: action.intersectPointLocal.x,
              y: action.intersectPointLocal.y,
              z: action.intersectPointLocal.z,
            },
            normalLocal: {
              x: action.normalLocal.x,
              y: action.normalLocal.y,
              z: action.normalLocal.z,
            },
            brushSize: action.brushSize,
            drillShape: action.drillShape || "circle",
          };
        }
        if (action.type === "scalpel") {
          return {
            type: "scalpel",
            meshes: action.meshes,
            cutHeight: action.cutHeight,
            drawnShape: action.drawnShape.map((pt) => ({
              x: pt.x,
              y: pt.y,
              z: pt.z,
            })),
            normal: {
              x: action.normal.x,
              y: action.normal.y,
              z: action.normal.z,
            },
            pointsLocal: Object.fromEntries(
              Object.entries(action.pointsLocal || {}).map(([name, arr]) => [
                name,
                arr.map((p) => ({ x: p.x, y: p.y, z: p.z })),
              ])
            ),
            normalsLocal: Object.fromEntries(
              Object.entries(action.normalsLocal || {}).map(([name, n]) => [
                name,
                { x: n.x, y: n.y, z: n.z },
              ])
            ),
          };
        }
        return null;
      })
      .filter(Boolean);
  }

  // SAVE EXISTING SLOT (clears undo history after success)
  const saveSurgeryPosition = useCallback(
    async (posId) => {
      if (!posId) {
        console.warn(
          "No surgery position selected. Please add or select one before saving."
        );
        return;
      }

      const actionsSlice = undoStack.current.slice(0, undoIndex.current);
      const actionsToSave = buildDeterministicActions(actionsSlice);

      const currentPosition = cameraRef.current.position.clone();
      const currentTarget = controlsRef.current.target.clone();
      const currentQuaternion = cameraRef.current.quaternion.clone();
      const currentUp = cameraRef.current.up.clone();

      const cameraPos = {
        position: {
          x: currentPosition.x,
          y: currentPosition.y,
          z: currentPosition.z,
        },
        target: {
          x: currentTarget.x,
          y: currentTarget.y,
          z: currentTarget.z,
        },
        quaternion: {
          x: currentQuaternion.x,
          y: currentQuaternion.y,
          z: currentQuaternion.z,
          w: currentQuaternion.w,
        },
        up: {
          x: currentUp.x,
          y: currentUp.y,
          z: currentUp.z,
        },
      };

      const opacities = snapshotOpacitiesByName();

      const currentSurgeryPos = selectedCaseDetail?.surgeryPos || {};
      const existingActions = currentSurgeryPos[posId]?.actions || [];
      const newActions = [...existingActions, ...actionsToSave];

      const surgeryPosData = { actions: newActions, cameraPos, opacities };

      const surgeryPosRef = ref(
        database,
        `caseDetails/${selectedCase.id}/surgeryPos/${posId}`
      );

      try {
        if (!selectedCase.isDemo) await update(surgeryPosRef, surgeryPosData);

        const updatedSurgeryPos = {
          ...currentSurgeryPos,
          [posId]: { ...(currentSurgeryPos[posId] || {}), ...surgeryPosData },
        };
        dispatch(updateSurgeryPos({ surgeryPos: updatedSurgeryPos }));

        // ✅ clear undo history after a successful save
        undoStack.current = [];
        undoIndex.current = 0;
      } catch (err) {
        console.error("Error saving surgery position:", err);
      }
    },
    [
      undoStack,
      undoIndex,
      selectedCase,
      selectedCaseDetail,
      dispatch,
      cameraRef,
      controlsRef,
      snapshotOpacitiesByName,
    ]
  );

  // CREATE + SAVE NOW in one atomic write (clears undo history after success)
  const createAndSaveSurgeryPos = useCallback(
    async (name) => {
      const currentSurgeryPos = selectedCaseDetail?.surgeryPos || {};
      if (Object.keys(currentSurgeryPos).length >= 5) {
        console.warn("Maximum surgery positions reached.");
        return;
      }

      const actionsSlice = undoStack.current.slice(0, undoIndex.current);
      const actionsToSave = buildDeterministicActions(actionsSlice);

      const currentPosition = cameraRef.current.position.clone();
      const currentTarget = controlsRef.current.target.clone();
      const currentQuaternion = cameraRef.current.quaternion.clone();
      const currentUp = cameraRef.current.up.clone();

      const cameraPos = {
        position: {
          x: currentPosition.x,
          y: currentPosition.y,
          z: currentPosition.z,
        },
        target: {
          x: currentTarget.x,
          y: currentTarget.y,
          z: currentTarget.z,
        },
        quaternion: {
          x: currentQuaternion.x,
          y: currentQuaternion.y,
          z: currentQuaternion.z,
          w: currentQuaternion.w,
        },
        up: {
          x: currentUp.x,
          y: currentUp.y,
          z: currentUp.z,
        },
      };

      const opacities = snapshotOpacitiesByName();

      const newSurgeryPosId = push(
        ref(database, `caseDetails/${selectedCase.id}/surgeryPos`)
      ).key;

      const surgeryPosData = {
        name,
        actions: actionsToSave,
        cameraPos,
        opacities,
      };

      const surgeryPosRef = ref(
        database,
        `caseDetails/${selectedCase.id}/surgeryPos/${newSurgeryPosId}`
      );

      try {
        if (!selectedCase.isDemo) await update(surgeryPosRef, surgeryPosData);

        const updatedSurgeryPos = {
          ...currentSurgeryPos,
          [newSurgeryPosId]: surgeryPosData,
        };
        dispatch(updateSurgeryPos({ surgeryPos: updatedSurgeryPos }));
        setSelectedSurgeryPosId(newSurgeryPosId);

        // ✅ clear undo history after a successful create+save
        undoStack.current = [];
        undoIndex.current = 0;
      } catch (err) {
        console.error("Error creating/saving surgery position:", err);
      } finally {
        setIsAddSurgeryPosModalOpen(false);
      }
    },
    [
      selectedCase,
      selectedCaseDetail,
      dispatch,
      undoStack,
      undoIndex,
      cameraRef,
      controlsRef,
      snapshotOpacitiesByName,
    ]
  );

  const deleteSurgeryPos = useCallback(
    (surgeryPosId) => {
      try {
        if (!selectedCase.isDemo) {
          const surgeryPosRef = ref(
            database,
            `caseDetails/${selectedCase.id}/surgeryPos/${surgeryPosId}`
          );
          remove(surgeryPosRef);
        }
        setSelectedSurgeryPosId((prevId) =>
          prevId === surgeryPosId ? null : prevId
        );
        const currentSurgeryPos = selectedCaseDetail?.surgeryPos || {};
        const updatedSurgeryPos = { ...currentSurgeryPos };
        delete updatedSurgeryPos[surgeryPosId];
        dispatch(updateSurgeryPos({ surgeryPos: updatedSurgeryPos }));
      } catch (err) {
        console.error("Error deleting surgery position:", err);
      }
    },
    [selectedCase, selectedCaseDetail, dispatch]
  );

  return {
    goToSurgeryPosition,
    saveSurgeryPosition,
    createAndSaveSurgeryPos,
    deleteSurgeryPos,
    isSurgeryPosMode,
    setIsSurgeryPosMode,
    selectedSurgeryPosId,
    setSelectedSurgeryPosId,
    isAddSurgeryPosModalOpen,
    setIsAddSurgeryPosModalOpen,
  };
};

export default useSurgeryPos;
